import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import api from "./api/client";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CustomerProvider } from "./context/CustomerContext";
import { LicenseProvider } from "./context/LicenseContext";
import SetupWizard from "./pages/SetupWizard";
import { PortalAuthProvider } from "./context/PortalAuthContext";
import Layout from "./components/Layout";
import LicenseGate from "./components/LicenseGate";
import LicenseNotices from "./components/LicenseNotices";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SsoReturn from "./pages/SsoReturn";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Devices from "./pages/Devices";
import Alerts from "./pages/Alerts";
import MaintenanceSchedules from "./pages/MaintenanceSchedules";
import RebootSchedules from "./pages/RebootSchedules";
import Certificates from "./pages/Certificates";
import UrlMonitoring from "./pages/UrlMonitoring";
import MonitoringSettings from "./pages/MonitoringSettings";
import DeviceDetail from "./pages/DeviceDetail";
import Onboarding from "./pages/Onboarding";
import Users from "./pages/Users";
import RolesAccess from "./pages/RolesAccess";
import ADUsers from "./pages/ADUsers";
import ToolsDns from "./pages/ToolsDns";
import ToolsDhcp from "./pages/ToolsDhcp";
import ToolsGpo from "./pages/ToolsGpo";
import Remediation from "./pages/Remediation";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import Calendar from "./pages/Calendar";
import Settings from "./pages/Settings";
import BackupRestore from "./pages/BackupRestore";
import PatchCompliance from "./pages/PatchCompliance";
import DeviceStability from "./pages/DeviceStability";
import ScheduledDocuments from "./pages/ScheduledDocuments";
import M365 from "./pages/M365";
import CloudStorage from "./pages/CloudStorage";
import CloudConnectors from "./pages/CloudConnectors";
import ChatIntegration from "./pages/ChatIntegration";
import CloudIdentity from "./pages/CloudIdentity";
import CloudCompute from "./pages/CloudCompute";
import CloudNetwork from "./pages/CloudNetwork";
import Scripts from "./pages/Scripts";
import DeviceShell from "./pages/DeviceShell";
import ConsoleWatchPage from "./pages/ConsoleWatchPage";
import ScreenSharePage from "./pages/ScreenSharePage";
import Software from "./pages/Software";
import Network from "./pages/Network";
import Security from "./pages/Security";
import AuditLog from "./pages/AuditLog";
import ReportsBuilder from "./pages/ReportsBuilder";
import HelpGuide from "./pages/HelpGuide";
import AISettings from "./pages/AISettings";
import AIActions from "./pages/AIActions";
import AICompliance from "./pages/AICompliance";
import IntuneDevices from "./pages/IntuneDevices";
import PSAArAging from "./pages/PSAArAging";
import PSAContracts from "./pages/PSAContracts";
import PSAQuotes from "./pages/PSAQuotes";
import PSAInvoices from "./pages/PSAInvoices";
import PSAPipeline from "./pages/PSAPipeline";
import PSARateCards from "./pages/PSARateCards";
import License from "./pages/License";
import MobileDashboard from "./pages/MobileDashboard";
import MerakiLicenseSummary from "./pages/MerakiLicenseSummary";
import MerakiController from "./pages/MerakiController";
import Licensing from "./pages/Licensing";
import AITerminal from "./pages/AITerminal";
import NetworkDiscovery from "./pages/NetworkDiscovery";
import CustomDashboards from "./pages/CustomDashboards";
import EmailIntake from "./pages/EmailIntake";
import HelpDeskSetup from "./pages/HelpDeskSetup";
import HelpDeskSettings from "./pages/HelpDeskSettings";
import M365Tenants from "./pages/M365Tenants";
import EntraUsers from "./pages/EntraUsers";
import InboundChannels from "./pages/InboundChannels";
import PSAIntegrations from "./pages/PSAIntegrations";
import Integrations from "./pages/Integrations";
import IntegrationsWebhooks from "./pages/IntegrationsWebhooks";
import PortalLayout from "./pages/portal/PortalLayout";
import PortalLogin from "./pages/portal/PortalLogin";
import PortalSetPassword from "./pages/portal/PortalSetPassword";
import PortalSsoReturn from "./pages/portal/PortalSsoReturn";
import PortalSecurity from "./pages/portal/PortalSecurity";
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalTickets from "./pages/portal/PortalTickets";
import PortalTicketDetail from "./pages/portal/PortalTicketDetail";
import PortalDevices from "./pages/portal/PortalDevices";
import PortalInvoices from "./pages/portal/PortalInvoices";
import PortalManage from "./pages/portal/PortalManage";
import PortalReports from "./pages/portal/PortalReports";
import PortalRunbooks from "./pages/portal/PortalRunbooks";
import PortalAudit from "./pages/portal/PortalAudit";

function Protected({
  children,
  adminOnly = false,
  feature,
}: {
  children: React.ReactNode;
  /** AEROGAUGE-PLATFORM administration only — NOT device administration.
   *
   *  "The admin role just means the admin of AeroGauge. Technicians need to
   *  be able to administer the devices." So this flag belongs on licensing,
   *  users, roles, settings, backup, audit and channel config: the things
   *  that govern the product itself. Anything under /devices/:id is a
   *  technician's job and must not carry it.
   *
   *  Two device routes did, and one of them was a live bug: the console
   *  button was widened to technicians but /devices/:id/screen still bounced
   *  them to "/", so the button appeared and then did nothing. */
  adminOnly?: boolean;
  /** Gate on a Roles & access feature instead of hardcoding a role.
   *
   *  The nav filter and this guard are two independent gates. When only one
   *  of them knew about a feature, the menu offered a page the router then
   *  refused and bounced you to "/" — which reads as the product being
   *  broken. Both now read the same effective feature list. */
  feature?: string;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // MFA enforcement: when the appliance requires MFA for this role and the
  // user hasn't enrolled, force them to Security to set it up first.
  if (user.mfa_required && location.pathname !== "/security")
    return <Navigate to="/security" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  // An admin always has everything, and a missing feature list (an older
  // token) must not lock anybody out — the server is the real gate, this is
  // just routing.
  if (
    feature &&
    user.role !== "admin" &&
    Array.isArray(user.features) &&
    !user.features.includes(feature)
  )
    return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

/**
 * Gate the tech UI behind first-run setup. Polls the always-open
 * /api/v1/setup/status; if the appliance isn't configured yet it renders
 * the wizard instead of the app (and instead of login, which would be
 * blocked by the backend setup gate). Fails open if the status check
 * errors so a transient blip never traps the operator out of the app.
 */
function SetupGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<"loading" | "setup" | "ready">("loading");
  useEffect(() => {
    api
      .get("/api/v1/setup/status")
      .then((r) => setState(r.data?.setup_complete ? "ready" : "setup"))
      .catch(() => setState("ready"));
  }, []);
  if (state === "loading")
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>
    );
  if (state === "setup") return <SetupWizard />;
  return <>{children}</>;
}

// BareProtected (auth without the Layout chrome) was removed: /help was its
// only consumer, and Help now renders in-layout as a sidebar destination.

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Customer portal — own auth provider, own layout */}
        <Route
          path="/portal/login"
          element={
            <PortalAuthProvider>
              <PortalLogin />
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/sso/return"
          element={
            <PortalAuthProvider>
              <PortalSsoReturn />
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/set-password"
          element={
            <PortalAuthProvider>
              <PortalSetPassword />
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalDashboard />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/tickets"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalTickets />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/tickets/:id"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalTicketDetail />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/devices"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalDevices />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/invoices"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalInvoices />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/manage"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalManage />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/reports"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalReports />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/runbooks"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalRunbooks />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/audit"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalAudit />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />
        <Route
          path="/portal/security"
          element={
            <PortalAuthProvider>
              <PortalLayout>
                <PortalSecurity />
              </PortalLayout>
            </PortalAuthProvider>
          }
        />

        {/* Tech UI */}
        <Route
          path="/*"
          element={
            <SetupGate>
              <AuthProvider>
                <CustomerProvider>
                  <LicenseProvider>
                    <LicenseNotices />
                    <LicenseGate>
                      <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/sso/return" element={<SsoReturn />} />
                        <Route
                          path="/"
                          element={
                            <Protected>
                              <Dashboard />
                            </Protected>
                          }
                        />
                        <Route
                          path="/customers"
                          element={
                            <Protected>
                              <Customers />
                            </Protected>
                          }
                        />
                        <Route
                          path="/customers/:id"
                          element={
                            <Protected>
                              <CustomerDetail />
                            </Protected>
                          }
                        />
                        <Route
                          path="/devices"
                          element={
                            <Protected>
                              <Devices />
                            </Protected>
                          }
                        />
                        <Route
                          path="/devices/:id"
                          element={
                            <Protected>
                              <DeviceDetail />
                            </Protected>
                          }
                        />
                        <Route
                          path="/alerts"
                          element={
                            <Protected>
                              <Alerts />
                            </Protected>
                          }
                        />
                        <Route
                          path="/monitoring/maintenance"
                          element={
                            <Protected>
                              <MaintenanceSchedules />
                            </Protected>
                          }
                        />
                        <Route
                          path="/monitoring/reboots"
                          element={
                            <Protected>
                              <RebootSchedules />
                            </Protected>
                          }
                        />
                        <Route
                          path="/monitoring/certificates"
                          element={
                            <Protected>
                              <Certificates />
                            </Protected>
                          }
                        />
                        <Route
                          path="/monitoring/urls"
                          element={
                            <Protected>
                              <UrlMonitoring />
                            </Protected>
                          }
                        />
                        <Route
                          path="/monitoring/settings"
                          element={
                            <Protected feature="monitoring">
                              <MonitoringSettings />
                            </Protected>
                          }
                        />
                        <Route
                          path="/onboarding"
                          element={
                            <Protected>
                              <Onboarding />
                            </Protected>
                          }
                        />
                        <Route
                          path="/tickets"
                          element={
                            <Protected>
                              <Tickets />
                            </Protected>
                          }
                        />
                        <Route
                          path="/tickets/:id"
                          element={
                            <Protected>
                              <TicketDetail />
                            </Protected>
                          }
                        />
                        <Route
                          path="/calendar"
                          element={
                            <Protected>
                              <Calendar />
                            </Protected>
                          }
                        />
                        <Route
                          path="/active-directory"
                          element={
                            <Protected>
                              <ADUsers />
                            </Protected>
                          }
                        />
                        <Route
                          path="/tools/dns"
                          element={
                            <Protected>
                              <ToolsDns />
                            </Protected>
                          }
                        />
                        <Route
                          path="/tools/dhcp"
                          element={
                            <Protected>
                              <ToolsDhcp />
                            </Protected>
                          }
                        />
                        <Route
                          path="/tools/gpo"
                          element={
                            <Protected>
                              <ToolsGpo />
                            </Protected>
                          }
                        />
                        <Route
                          path="/remediation"
                          element={
                            <Protected>
                              <Remediation />
                            </Protected>
                          }
                        />
                        <Route
                          path="/patch-compliance"
                          element={
                            <Protected>
                              <PatchCompliance />
                            </Protected>
                          }
                        />
                        <Route
                          path="/device-stability"
                          element={
                            <Protected>
                              <DeviceStability />
                            </Protected>
                          }
                        />
                        <Route
                          path="/m365"
                          element={
                            <Protected feature="m365">
                              <M365 />
                            </Protected>
                          }
                        />
                        <Route
                          path="/cloud"
                          element={
                            <Protected feature="network">
                              <CloudConnectors />
                            </Protected>
                          }
                        />
                        <Route
                          path="/cloud/compute"
                          element={
                            <Protected feature="network">
                              <CloudCompute />
                            </Protected>
                          }
                        />
                        <Route
                          path="/cloud/network"
                          element={
                            <Protected feature="network">
                              <CloudNetwork />
                            </Protected>
                          }
                        />
                        <Route
                          path="/cloud/identity"
                          element={
                            <Protected feature="m365">
                              <CloudIdentity />
                            </Protected>
                          }
                        />
                        <Route
                          path="/cloud-storage"
                          element={
                            <Protected feature="m365">
                              <CloudStorage />
                            </Protected>
                          }
                        />
                        {/* The capability group is "storage" and the browser
                            has always lived at /cloud-storage. Anything
                            deriving a link from the group key lands here, so
                            the two spellings must both resolve rather than
                            silently bouncing to the dashboard. */}
                        <Route path="/cloud/storage" element={<Navigate to="/cloud-storage" replace />} />
                        <Route
                          path="/scripts"
                          element={
                            <Protected>
                              <Scripts />
                            </Protected>
                          }
                        />
                        <Route
                          path="/devices/:id/shell"
                          element={
                            <Protected>
                              <DeviceShell />
                            </Protected>
                          }
                        />
                        <Route
                          path="/devices/:id/screen"
                          element={
                            <Protected>
                              <ScreenSharePage />
                            </Protected>
                          }
                        />
                        {/* Watching a session you were invited to. Deliberately
                            NOT adminOnly - inviting a technician is the point -
                            and deliberately not device-scoped: the server
                            resolves the device from the session token, and the
                            invite decides whether you may see it at all. */}
                        <Route
                          path="/console/:kind/:token/watch"
                          element={
                            <Protected>
                              <ConsoleWatchPage />
                            </Protected>
                          }
                        />
                        <Route
                          path="/software"
                          element={
                            <Protected>
                              <Software />
                            </Protected>
                          }
                        />
                        <Route
                          path="/network"
                          element={
                            <Protected>
                              <Network />
                            </Protected>
                          }
                        />
                        <Route
                          path="/security"
                          element={
                            <Protected>
                              <Security />
                            </Protected>
                          }
                        />
                        <Route
                          path="/license"
                          element={
                            <Protected adminOnly>
                              <License />
                            </Protected>
                          }
                        />
                        <Route
                          path="/reports"
                          element={
                            <Protected>
                              <ReportsBuilder />
                            </Protected>
                          }
                        />
                        <Route
                          path="/scheduled-documents"
                          element={
                            <Protected>
                              <ScheduledDocuments />
                            </Protected>
                          }
                        />
                        <Route
                          path="/audit"
                          element={
                            <Protected adminOnly>
                              <AuditLog />
                            </Protected>
                          }
                        />
                        <Route
                          path="/users"
                          element={
                            <Protected adminOnly>
                              <Users />
                            </Protected>
                          }
                        />
                        <Route
                          path="/roles-access"
                          element={
                            <Protected adminOnly>
                              <RolesAccess />
                            </Protected>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <Protected adminOnly>
                              <Settings />
                            </Protected>
                          }
                        />
                        <Route
                          path="/backup"
                          element={
                            <Protected adminOnly>
                              <BackupRestore />
                            </Protected>
                          }
                        />
                        <Route
                          path="/ai-settings"
                          element={
                            <Protected feature="ai">
                              <AISettings />
                            </Protected>
                          }
                        />
                        <Route
                          path="/ai-actions"
                          element={
                            <Protected>
                              <AIActions />
                            </Protected>
                          }
                        />
                        <Route
                          path="/ai-compliance"
                          element={
                            <Protected>
                              <AICompliance />
                            </Protected>
                          }
                        />
                        <Route
                          path="/intune"
                          element={
                            <Protected>
                              <IntuneDevices />
                            </Protected>
                          }
                        />
                        <Route
                          path="/psa/contracts"
                          element={
                            <Protected>
                              <PSAContracts />
                            </Protected>
                          }
                        />
                        <Route
                          path="/psa/quotes"
                          element={
                            <Protected>
                              <PSAQuotes />
                            </Protected>
                          }
                        />
                        <Route
                          path="/psa/invoices"
                          element={
                            <Protected>
                              <PSAInvoices />
                            </Protected>
                          }
                        />
                        <Route
                          path="/psa/pipeline"
                          element={
                            <Protected>
                              <PSAPipeline />
                            </Protected>
                          }
                        />
                        <Route
                          path="/psa/rate-cards"
                          element={
                            <Protected>
                              <PSARateCards />
                            </Protected>
                          }
                        />
                        <Route
                          path="/psa/ar-aging"
                          element={
                            <Protected>
                              <PSAArAging />
                            </Protected>
                          }
                        />
                        <Route
                          path="/mobile"
                          element={
                            <Protected>
                              <MobileDashboard />
                            </Protected>
                          }
                        />
                        <Route
                          path="/meraki-licenses"
                          element={
                            <Protected>
                              <MerakiLicenseSummary />
                            </Protected>
                          }
                        />
                        <Route
                          path="/meraki-cloud"
                          element={
                            <Protected>
                              <MerakiController />
                            </Protected>
                          }
                        />
                        <Route
                          path="/licensing"
                          element={
                            <Protected>
                              <Licensing />
                            </Protected>
                          }
                        />
                        <Route
                          path="/ai-terminal"
                          element={
                            // Its own RBAC feature, so it can be withheld
                            // without withholding the rest of AI. Gated here
                            // as well as in the nav: narrowing only the menu
                            // leaves the page reachable by URL.
                            <Protected feature="ai_terminal">
                              <AITerminal />
                            </Protected>
                          }
                        />
                        <Route
                          path="/network-discovery"
                          element={
                            <Protected>
                              <NetworkDiscovery />
                            </Protected>
                          }
                        />
                        <Route
                          path="/my-dashboards"
                          element={
                            <Protected>
                              <CustomDashboards />
                            </Protected>
                          }
                        />
                        <Route
                          path="/helpdesk/email-intake"
                          element={
                            <Protected feature="helpdesk">
                              <EmailIntake />
                            </Protected>
                          }
                        />
                        {/* Moved under HelpDesk. Kept as a redirect so existing
                            bookmarks and any links still in flight land in the
                            right place instead of on the not-found page. */}
                        <Route
                          path="/email-intake"
                          element={<Navigate to="/helpdesk/email-intake" replace />}
                        />
                        <Route
                          path="/helpdesk/settings"
                          element={
                            <Protected feature="helpdesk">
                              <HelpDeskSettings />
                            </Protected>
                          }
                        />
                        <Route
                          path="/helpdesk/setup"
                          element={
                            <Protected feature="helpdesk">
                              <HelpDeskSetup />
                            </Protected>
                          }
                        />
                        <Route
                          path="/m365-tenants"
                          element={
                            // WAS admin-only, on the reasoning that 7 of the 10
                            // endpoints behind it required admin anyway. That
                            // hid the page instead of fixing the endpoints, and
                            // it is what stopped a technician syncing a tenant.
                            // The endpoints admit technicians now, so this is
                            // driven by the RBAC feature like every other
                            // screen - the matrix decides, not a literal role.
                            <Protected feature="m365">
                              <M365Tenants />
                            </Protected>
                          }
                        />
                        <Route
                          path="/entra-users"
                          element={
                            <Protected>
                              <EntraUsers />
                            </Protected>
                          }
                        />
                        <Route
                          path="/inbound-channels"
                          element={
                            <Protected adminOnly>
                              <InboundChannels />
                            </Protected>
                          }
                        />
                        <Route
                          path="/psa-integrations"
                          element={
                            <Protected feature="integrations">
                              <PSAIntegrations />
                            </Protected>
                          }
                        />
                        <Route
                          path="/integrations"
                          element={
                            <Protected feature="integrations">
                              <Integrations />
                            </Protected>
                          }
                        />
                        <Route
                          path="/integrations/chat"
                          element={
                            // adminOnly dropped: it is checked BEFORE the
                            // feature, so carrying both made the feature grant
                            // dead weight on this route.
                            <Protected feature="integrations">
                              <ChatIntegration />
                            </Protected>
                          }
                        />
                        {/* Both halves of Slack/Teams used to be their own
                            menu entry, sitting next to each other and doing
                            opposite things. They are one screen now; these
                            keep bookmarks and the generated docs working. */}
                        <Route
                          path="/integrations/webhooks"
                          element={
                            <Protected adminOnly>
                              <IntegrationsWebhooks />
                            </Protected>
                          }
                        />
                        <Route
                          path="/help"
                          element={
                            // Protected, not BareProtected: Help is a normal
                            // sidebar destination now, so it renders inside the
                            // Layout. It used to open in a new tab, where a
                            // chrome-less page was right; navigating to one from
                            // the menu would strand you with no way back.
                            <Protected>
                              <HelpGuide />
                            </Protected>
                          }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </LicenseGate>
                  </LicenseProvider>
                </CustomerProvider>
              </AuthProvider>
            </SetupGate>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
